import { html } from "lit";
import type { RouteConfig } from "@lit-labs/router";

import { pb } from "./lib/pb";
import { navigate } from "./lib/navigation";

import "./pages/login-page";
import "./pages/companies-page";
import "./pages/company-detail-page";
import "./pages/provisioning-page";
import "./pages/ebsi-onboarding-page";
import "./pages/counterfeit-page";
import "./pages/authorities-page";
import "./pages/not-found-page";

type Guard = () => Promise<boolean>;

/** Only reachable when signed out; signed-in operators land on the
 * anti-counterfeit register — the leading oversight tier. */
const requireGuest: Guard = async () => {
  if (pb.authStore.isValid) {
    await navigate("/counterfeit");
    return false;
  }
  return true;
};

/** Requires a signed-in operator. The funnel APIs are platform_operator-gated
 * server-side; the login page rejects non-operators, so a valid session here is
 * an operator session. */
const requireOperator: Guard = async () => {
  if (!pb.authStore.isValid) {
    await navigate("/login");
    return false;
  }
  return true;
};

export const routes: RouteConfig[] = [
  {
    path: "/login",
    enter: requireGuest,
    render: () => html`<login-page></login-page>`,
  },
  {
    path: "/",
    enter: requireOperator,
    render: () => html`<counterfeit-page></counterfeit-page>`,
  },
  {
    path: "/companies",
    enter: requireOperator,
    render: () => html`<companies-page></companies-page>`,
  },
  {
    path: "/companies/:id",
    enter: requireOperator,
    render: ({ id }) =>
      html`<company-detail-page .companyId=${id ?? ""}></company-detail-page>`,
  },
  {
    path: "/provisioning",
    enter: requireOperator,
    render: () => html`<provisioning-page></provisioning-page>`,
  },
  {
    path: "/counterfeit",
    enter: requireOperator,
    render: () => html`<counterfeit-page></counterfeit-page>`,
  },
  {
    path: "/authorities",
    enter: requireOperator,
    render: () => html`<authorities-page></authorities-page>`,
  },
  {
    path: "/provisioning/ebsi/:id",
    enter: requireOperator,
    render: ({ id }) =>
      html`<ebsi-onboarding-page
        .prospectId=${id ?? ""}
      ></ebsi-onboarding-page>`,
  },
];

/** Always-matches fallback for unknown paths. */
export const fallbackRoute = {
  render: () => html`<not-found-page></not-found-page>`,
};
